import { createSlice } from '@reduxjs/toolkit';
import { getShiftStatus } from '../../utils/shiftUtils';
import { getStartOfWeek, formatLocalDate } from '../../utils/dateUtils';
import { SHIFT_STATUS } from '../../utils/constants/shiftConstants';

const initialState = {
  shifts: [],
  shiftsByWeek: {}, // New state to hold shifts grouped by week
  shiftsByAssignment: {
    unassigned: {},
    assigned: {},
  },
  selectedWeek: null,
  status: 'idle',
  shiftStatus: {}, // Stores detailed information for each shift by ID
  shiftInProgress: null,
  lateShifts: [],
  breaks: null,
  error: null,
  loading: false,
  availability: {},
};

const shiftsSlice = createSlice({
  name: 'shifts',
  initialState,
  reducers: {
    fetchShiftsStart(state) {
      state.status = 'loading';
      state.loading = true;
    },
    fetchShiftsSuccess(state, action) {
      state.status = 'succeeded';
      state.shifts = action.payload;
      state.loading = false;

      // Group shifts by assignment (new logic)
      state.shiftsByAssignment = action.payload.reduce(
        (acc, shift) => {
          const isAssigned = !!shift.eid; // Assuming `eid` indicates if the shift is assigned
          if (isAssigned) {
            if (!acc.assigned[shift.eid]) {
              acc.assigned[shift.eid] = [];
            }
            acc.assigned[shift.eid].push(shift);
          } else {
            // Ensure that the role is properly grouped in unassigned
            const role = shift.name || 'Undefined Role'; // Fallback to "Undefined Role" if no role is specified
            if (!acc.unassigned[role]) {
              acc.unassigned[role] = [];
            }
            acc.unassigned[role].push(shift);
          }
          return acc;
        },
        { unassigned: {}, assigned: {} }
      );

      // Set the shift status for each shift, including detailed info
      state.shiftStatus = action.payload.reduce((acc, shift) => {
        const status = getShiftStatus(shift);
        // Validate that the status is one of our expected values
        if (!Object.values(SHIFT_STATUS).includes(status)) {
          console.warn(`Unexpected shift status: ${status} for shift ${shift.id}`);
        }

        acc[shift.id] = {
          id: shift.id,
          date: formatLocalDate(shift.start, {
            weekday: 'long',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          }),
          status,
          breaks: shift.breaks,
        };
        return acc;
      }, {});
    },
    fetchShiftsFailure(state, action) {
      state.status = 'failed';
      state.error = action.payload;
      state.loading = false;
    },
    fetchShiftsByEmployeeSuccess(state, action) {
      const { shifts, startOfWeekIndex } = action.payload;

      state.status = 'succeeded';
      state.loading = false;

      // Group shifts by week (existing logic)
      state.shiftsByWeek = shifts.reduce((acc, shift) => {
        const week = getStartOfWeek(shift.start, startOfWeekIndex);
        if (!acc[week]) {
          acc[week] = [];
        }
        acc[week].push(shift);
        return acc;
      }, {});
    },
    createShiftStart(state) {
      state.status = 'loading';
      state.loading = true;
    },
    createShiftSuccess(state, action) {
      state.status = 'succeeded';
      state.shifts.push(action.payload);
      state.loading = false;
    },
    createShiftFailure(state, action) {
      state.status = 'failed';
      state.error = action.payload;
      state.loading = false;
    },
    updateShift(state, action) {
      const { id, updatedShiftRedux, startOfWeekIndex } = action.payload;
      const existingShift = state.shifts.find((shift) => shift.id === id);

      if (existingShift) {
        Object.assign(existingShift, updatedShiftRedux);
      }

      // Update in grouped shifts
      const week = getStartOfWeek(updatedShiftRedux.start, startOfWeekIndex);
      if (state.shiftsByWeek[week]) {
        const index = state.shiftsByWeek[week].findIndex((shift) => shift.id === id);
        if (index !== -1) {
          state.shiftsByWeek[week][index] = updatedShiftRedux;
        }
      }

      // Update the shift status with detailed info
      const status = getShiftStatus(updatedShiftRedux);
      // Validate that the status is one of our expected values
      if (!Object.values(SHIFT_STATUS).includes(status)) {
        console.warn(`Unexpected shift status: ${status} for shift ${id}`);
      }

      state.shiftStatus[id] = {
        id,
        date: formatLocalDate(updatedShiftRedux.start, {
          weekday: 'long',
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        }),
        status,
      };
    },
    updateShiftBreaks(state, action) {
      const { shiftId, breaks } = action.payload;
      const shiftIndex = state.shifts.findIndex((s) => s.id === shiftId);

      if (shiftIndex !== -1) {
        const updatedShift = {
          ...state.shifts[shiftIndex],
          breaks, // Actualiza solo los breaks
        };

        // Update the state with the new shift
        state.shifts[shiftIndex] = updatedShift;
      }
    },
    selectedWeek(state, action) {
      const startOfWeek = new Date(action.payload);
      startOfWeek.setHours(0, 0, 0, 0);

      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);

      state.selectedWeek = {
        start: startOfWeek.toISOString(),
        end: endOfWeek.toISOString(),
      };

      localStorage.setItem(
        'currentWeek',
        JSON.stringify({
          start: state.selectedWeek.start,
          end: state.selectedWeek.end,
        })
      );
    },
    updateShiftStart(state) {
      state.status = 'loading';
      state.loading = true;
    },
    updateShiftSuccess(state, action) {
      state.status = 'succeeded';
      state.shifts.push(action.payload);
      state.loading = false;
    },
    updateShiftFailure(state, action) {
      state.status = 'failed';
      state.error = action.payload;
      state.loading = false;
    },
    deleteShiftStart: (state) => {
      state.loading = 'loading';
      state.loading = true;
    },
    deleteShiftSuccess: (state) => {
      state.loading = 'succeeded';
      state.loading = false;
    },
    deleteShiftFailure: (state, action) => {
      state.loading = 'failed';
      state.error = action.payload;
      state.loading = false;
    },
    startShiftStart(state) {
      state.status = 'loading';
      state.loading = true;
    },
    startShiftSuccess(state, action) {
      state.status = 'succeeded';
      state.loading = false;

      // Set the shift in progress with the provided shift ID and status
      const { id, status } = action.payload;
      state.shiftInProgress = { id, status };
    },
    startShiftFailure(state, action) {
      state.status = 'failed';
      state.error = action.payload;
      state.loading = false;
    },
    endShiftStart(state) {
      state.status = 'loading';
      state.loading = true;
    },
    endShiftSuccess(state) {
      state.status = 'succeeded';
      state.loading = false;
    },
    endShiftFailure(state, action) {
      state.status = 'failed';
      state.error = action.payload;
      state.loading = false;
    },
    fetchAvailabilitySuccess(state, action) {
      state.availability = action.payload;
    },
  },
});

export const {
  fetchShiftsStart,
  fetchShiftsSuccess,
  fetchShiftsFailure,
  fetchShiftsByEmployeeSuccess,
  createShiftStart,
  createShiftSuccess,
  createShiftFailure,
  updateShift,
  updateShiftStart,
  updateShiftSuccess,
  updateShiftFailure,
  deleteShiftStart,
  deleteShiftSuccess,
  deleteShiftFailure,
  startShiftStart,
  startShiftSuccess,
  startShiftFailure,
  endShiftStart,
  endShiftSuccess,
  endShiftFailure,
  selectedWeek,
  updateShiftBreaks,
  fetchAvailabilitySuccess,
} = shiftsSlice.actions;

export default shiftsSlice.reducer;

export const selectAllShifts = (state) => state.shifts.shifts;
export const selectShiftsByWeek = (state) => state.shifts.shiftsByWeek;
export const selectShiftStatus = (state) => state.shifts.shiftStatus;
export const selectShiftsByAssignment = (state) => state.shifts.shiftsByAssignment;
export const selectShiftAvailability = (state) => state.shifts.availability;
export const currentWeek = (state) => state.shifts.selectedWeek;

export const selectShiftById = (state, shiftId) => {
  return state.shifts.shifts.find((shift) => shift.id === shiftId);
};
